import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { salesApi } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

type SaleItem = {
  id: string;
  amount: number | string;
  saleDate: string;
  client?: {
    name?: string;
  };
};

type DayCell = {
  day: number;
  dateKey: string;
  count: number;
  totalAmount: number;
  sales: Array<{ clientName: string; amount: number }>;
  isCurrentMonth: boolean;
  isToday: boolean;
};

function getMonthBounds(baseDate: Date) {
  const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function toDateKey(date: Date) {
  return date.toLocaleDateString('en-CA');
}

type SalesCalendarProps = {
  displayDate: Date;
  onDisplayDateChange: (next: Date) => void;
};

export function SalesCalendar({ displayDate, onDisplayDateChange }: SalesCalendarProps) {
  const { start, end } = useMemo(() => getMonthBounds(displayDate), [displayDate]);

  const { data, isLoading } = useQuery({
    queryKey: ['sales-calendar', start.toISOString(), end.toISOString()],
    queryFn: async () => {
      const limit = 100;
      let page = 1;
      let total = Number.POSITIVE_INFINITY;
      const allSales: SaleItem[] = [];

      while (allSales.length < total) {
        const response = await salesApi.getAll({
          page,
          limit,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          sortBy: 'saleDate',
          sortOrder: 'DESC',
        });

        const pageData = (response.data.data || []) as SaleItem[];
        total = response.data.total ?? pageData.length;
        allSales.push(...pageData);

        if (pageData.length < limit) {
          break;
        }

        page += 1;
      }

      return allSales;
    },
    retry: false,
  });

  const { cells, maxAmount, monthTotal, monthCount } = useMemo(() => {
    const monthSales = data || [];
    const amountByDay = new Map<
      string,
      { count: number; amount: number; sales: Array<{ clientName: string; amount: number }> }
    >();

    monthSales.forEach((sale) => {
      const date = new Date(sale.saleDate);
      const dateKey = toDateKey(date);
      const current = amountByDay.get(dateKey) ?? { count: 0, amount: 0, sales: [] };
      current.count += 1;
      const parsedAmount = Number(sale.amount);
      current.amount += parsedAmount;
      current.sales.push({
        clientName: sale.client?.name || 'Cliente',
        amount: parsedAmount,
      });
      amountByDay.set(dateKey, current);
    });

    const firstDay = new Date(start);
    const offset = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - offset);

    const builtCells: DayCell[] = [];
    for (let i = 0; i < 42; i += 1) {
      const current = new Date(gridStart);
      current.setDate(gridStart.getDate() + i);
      const dateKey = toDateKey(current);
      const salesDay = amountByDay.get(dateKey);
      const today = new Date();

      builtCells.push({
        day: current.getDate(),
        dateKey,
        count: salesDay?.count ?? 0,
        totalAmount: salesDay?.amount ?? 0,
        sales: salesDay?.sales ?? [],
        isCurrentMonth: current.getMonth() === start.getMonth(),
        isToday:
          current.getDate() === today.getDate() &&
          current.getMonth() === today.getMonth() &&
          current.getFullYear() === today.getFullYear(),
      });
    }

    return {
      cells: builtCells,
      maxAmount: Math.max(0, ...Array.from(amountByDay.values()).map((item) => item.amount)),
      monthTotal: monthSales.reduce((sum, item) => sum + Number(item.amount), 0),
      monthCount: monthSales.length,
    };
  }, [data, start]);

  const monthLabel = displayDate.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  const getIntensity = (amount: number) => {
    if (amount <= 0 || maxAmount === 0) return 'bg-gray-100 dark:bg-gray-800';
    const ratio = amount / maxAmount;

    if (ratio < 0.25) return 'bg-blue-100 dark:bg-blue-900/40';
    if (ratio < 0.5) return 'bg-blue-200 dark:bg-blue-800/55';
    if (ratio < 0.75) return 'bg-blue-300 dark:bg-blue-700/70';
    return 'bg-blue-500 text-white dark:bg-blue-500';
  };

  return (
    <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Calendario de ventas</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {monthCount} ventas registradas por {formatCurrency(monthTotal)} en {monthLabel}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onDisplayDateChange(new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1))}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium capitalize min-w-[140px] text-center">{monthLabel}</span>
          <button
            type="button"
            onClick={() => onDisplayDateChange(new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1))}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((label) => (
          <div key={label} className="text-center font-medium">
            {label}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 42 }).map((_, index) => (
            <div key={index} className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-7 gap-2"
        >
          {cells.map((cell) => (
            <div
              key={cell.dateKey}
              className={[
                'h-20 rounded-lg border p-1.5 text-xs transition-colors overflow-hidden',
                cell.isCurrentMonth
                  ? 'border-gray-200 dark:border-gray-700'
                  : 'border-transparent opacity-45',
                getIntensity(cell.totalAmount),
                cell.isToday ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-900' : '',
              ].join(' ')}
              title={
                cell.count > 0
                  ? `${cell.dateKey}: ${cell.count} venta(s), ${formatCurrency(cell.totalAmount)}`
                  : `${cell.dateKey}: sin ventas`
              }
            >
              <div className="font-medium">{cell.day}</div>
              {cell.count > 0 && (
                <div className="mt-1 space-y-0.5">
                  {cell.sales.slice(0, 2).map((sale, index) => (
                    <div key={`${cell.dateKey}-${index}`} className="truncate text-[10px] leading-tight">
                      {sale.clientName}: {formatCurrency(sale.amount)}
                    </div>
                  ))}
                  {cell.sales.length > 2 && (
                    <div className="text-[10px] opacity-80">+{cell.sales.length - 2} más</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </motion.div>
      )}
    </section>
  );
}
